import { UPDATE_COUPON_REDUCER } from "store/actionTypes";
import { ENDPOINTS } from "utils/endpoints";
import {
  mapCouponDetailView,
  mapCouponItem,
  mapCreateCouponPayload,
  mapPaginatedResult,
  mapUpdateCouponPayload,
} from "utils/apiMappers";
import { APIResponseModal, ReducersModal } from "utils/modals";
import SHOW_TOAST from "utils/showToast";
import {
  DELETE_API_CALL,
  DELETE_S3_FILES,
  GET_API_CALL,
  GET_READ_SIGNED_URLS,
  PATCH_API_CALL,
  POST_API_CALL,
} from "utils/services";
import {
  hasAwsSignedQuery,
  isLocalPreviewUrl,
  isStoredMediaUrl,
} from "utils/commonFunctions";
import { resolveCouponListCategory } from "utils/serviceCategory";

let listRequestKey = "";

const storedUrlForSigning = (url?: string) => {
  const value = `${url || ""}`.trim();
  if (!value || isLocalPreviewUrl(value) || hasAwsSignedQuery(value)) return "";
  if (!isStoredMediaUrl(value)) return "";
  return value;
};

const attachCouponImageUrls = async (items: any[] = []) => {
  const targets: number[] = [];
  const urls: string[] = [];

  items.forEach((item, index) => {
    const imageUrl = storedUrlForSigning(
      item?.image || item?.bannerImage || item?.imagePreview
    );
    if (imageUrl) {
      targets.push(index);
      urls.push(imageUrl);
    }
  });

  const withDefaults = items.map((item) => ({
    ...item,
    imagePreview: item?.imagePreview || item?.image || "",
    bannerImage: item?.bannerImage || item?.imagePreview || item?.image || "",
  }));

  if (!urls.length) return withDefaults;

  try {
    const signedUrls = await GET_READ_SIGNED_URLS(urls);
    return withDefaults.map((item, index) => {
      const signedIndex = targets.indexOf(index);
      if (signedIndex < 0) return item;
      const signed = signedUrls[signedIndex];
      if (!signed) return item;
      return {
        ...item,
        imagePreview: signed,
        bannerImage: signed,
      };
    });
  } catch {
    return withDefaults;
  }
};

export const dispatchCouponValue = (payload: any) => {
  return (dispatch: Function) => {
    dispatch({
      type: UPDATE_COUPON_REDUCER,
      payload,
    });
  };
};

export type GetCouponListOptions = {
  page?: number;
  limit?: number;
  search?: string;
  category?: string | number;
  status?: number;
  isolated?: boolean;
  callback?: (mapped: any) => void;
};

const emptyCouponPage = (page: number, limit: number) => ({
  result: [],
  page,
  limit,
  total: 0,
  totalPages: 0,
  pagination: { page, limit, totalPages: 0, totalRecords: 0 },
  nextHit: false,
});

const isolatedInflight: Record<string, Function[]> = {};

const fetchIsolatedCouponList = (options: GetCouponListOptions = {}) => {
  const page = options.page || 1;
  const limit = options.limit || 10;
  const search = `${options.search || ""}`.trim();
  const category = `${options.category ?? ""}`;

  if (!category || category === "-1") {
    options.callback?.(emptyCouponPage(page, limit));
    return;
  }

  const queryParts = [`page=${page}`, `limit=${limit}`, `category=${category}`];
  if (search) {
    queryParts.push(`search=${encodeURIComponent(search)}`);
  }
  if (options.status != null && `${options.status}` !== "") {
    queryParts.push(`status=${options.status}`);
  }
  const query = `?${queryParts.join("&")}`;

  if (isolatedInflight[query]) {
    if (options.callback) isolatedInflight[query].push(options.callback);
    return;
  }
  isolatedInflight[query] = options.callback ? [options.callback] : [];

  GET_API_CALL(ENDPOINTS.COUPON.LIST, query, (response: APIResponseModal) => {
    const callbacks = isolatedInflight[query] || [];
    delete isolatedInflight[query];
    const mapped = response.isSuccess
      ? mapPaginatedResult(response.data, mapCouponItem)
      : emptyCouponPage(page, limit);
    callbacks.forEach((cb) => cb(mapped));
  });
};

/** GET /api/v1/shinology/coupon/list?page&limit&search&category&status */
export const getCouponList = (options?: GetCouponListOptions) => {
  return (dispatch: Function, getState: Function) => {
    if (options?.isolated) {
      fetchIsolatedCouponList(options);
      return;
    }
    const {
      page,
      limit,
      search,
      category,
      result,
    }: ReducersModal["CouponReducer"] = getState().CouponReducer;

    const queryParts = [`page=${page || 1}`, `limit=${limit || 10}`];
    if (search?.trim()) {
      queryParts.push(`search=${encodeURIComponent(search.trim())}`);
    }
    if (category !== "-1" && category !== "" && category != null) {
      queryParts.push(`category=${category}`);
    }
    const query = `?${queryParts.join("&")}`;

    if (listRequestKey === query) {
      return;
    }
    listRequestKey = query;

    dispatch({
      type: UPDATE_COUPON_REDUCER,
      payload: { isLoading: true, result },
    });

    GET_API_CALL(
      ENDPOINTS.COUPON.LIST,
      query,
      (response: APIResponseModal) => {
        if (listRequestKey === query) listRequestKey = "";
        const current: ReducersModal["CouponReducer"] =
          getState().CouponReducer;
        const isStale =
          `${current.category}` !== `${category}` ||
          Number(current.page) !== Number(page) ||
          Number(current.limit) !== Number(limit) ||
          `${current.search || ""}` !== `${search || ""}`;
        if (isStale) {
          return;
        }

        if (response.isSuccess) {
          const mapped = mapPaginatedResult(response.data, mapCouponItem);
          attachCouponImageUrls(mapped.result).then((signedResult) => {
            const latest: ReducersModal["CouponReducer"] =
              getState().CouponReducer;
            if (
              `${latest.category}` !== `${category}` ||
              Number(latest.page) !== Number(page) ||
              Number(latest.limit) !== Number(limit) ||
              `${latest.search || ""}` !== `${search || ""}`
            ) {
              return;
            }
            dispatch({
              type: UPDATE_COUPON_REDUCER,
              payload: {
                result: signedResult,
                totalRecords: mapped.total,
                totalPages: mapped.totalPages,
                page: mapped.page,
                limit: mapped.limit,
                isLoading: false,
              },
            });
          });
          return;
        }

        dispatch({
          type: UPDATE_COUPON_REDUCER,
          payload: { isLoading: false, result: [] },
        });
      }
    );
  };
};

/** Resolve coupon _id by code without touching CouponReducer or isolated list key. */
export const fetchCouponIdByCode = ({
  couponCode,
  category,
  callback,
}: {
  couponCode?: string;
  category?: string | number | null;
  callback: (couponId?: string) => void;
}) => {
  return () => {
    const code = `${couponCode || ""}`.trim();
    const resolvedCategory = resolveCouponListCategory(category);
    if (!code || !resolvedCategory) {
      callback(undefined);
      return;
    }
    const query = `?page=1&limit=10&category=${resolvedCategory}&search=${encodeURIComponent(
      code
    )}`;
    GET_API_CALL(ENDPOINTS.COUPON.LIST, query, (response: APIResponseModal) => {
      if (!response.isSuccess) {
        callback(undefined);
        return;
      }
      const mapped = mapPaginatedResult(response.data, mapCouponItem);
      const match = (mapped.result || []).find(
        (item: any) => item.couponCode === code
      );
      callback(match?._id || undefined);
    });
  };
};

/** Isolated coupon lookup for Order Detail. Does not touch CouponReducer. */
export const fetchCouponForOrder = ({
  couponCode,
  couponId,
  category,
  callback,
}: {
  couponCode?: string;
  couponId?: string;
  category?: string | number | null;
  callback: (coupon: any | null) => void;
}) => {
  return () => {
    const id = `${couponId || ""}`.trim();
    const code = `${couponCode || ""}`.trim();
    const resolvedCategory = resolveCouponListCategory(category);

    const finishMapped = (raw: any) => {
      if (!raw) {
        callback(null);
        return;
      }
      const mapped = raw.couponTypeLabel ? raw : mapCouponDetailView(raw);
      attachCouponImageUrls([mapped]).then((items) => {
        callback(items[0] || mapped);
      });
    };

    const fetchByCode = () => {
      if (!code || !resolvedCategory) {
        callback(null);
        return;
      }
      const query = `?page=1&limit=10&category=${resolvedCategory}&search=${encodeURIComponent(
        code
      )}`;
      GET_API_CALL(ENDPOINTS.COUPON.LIST, query, (response: APIResponseModal) => {
        if (!response.isSuccess) {
          callback(null);
          return;
        }
        const mapped = mapPaginatedResult(response.data, mapCouponItem);
        const match =
          (mapped.result || []).find((item: any) => item.couponCode === code) ||
          null;
        finishMapped(match ? mapCouponDetailView(match) : null);
      });
    };

    if (id) {
      GET_API_CALL(
        `${ENDPOINTS.COUPON.DETAIL}/${id}`,
        "",
        (response: APIResponseModal) => {
          if (response.isSuccess && response.data) {
            const raw =
              response.data?._id || response.data?.couponCode
                ? response.data
                : response.data?.data ||
                  response.data?.result ||
                  response.data;
            finishMapped(mapCouponDetailView(raw));
            return;
          }
          fetchByCode();
        },
        { couponId: id }
      );
      return;
    }

    fetchByCode();
  };
};

/** GET /api/v1/shinology/coupon/detail/{couponId} */
export const getCouponDetails = (
  couponId: string,
  callback: Function = () => {},
  isolated = false
) => {
  return (dispatch: Function) => {
    if (!couponId) {
      callback(null);
      return;
    }

    if (!isolated) {
      dispatch({
        type: UPDATE_COUPON_REDUCER,
        payload: { isLoading: true },
      });
    }

    GET_API_CALL(
      `${ENDPOINTS.COUPON.DETAIL}/${couponId}`,
      "",
      (response: APIResponseModal) => {
        const finish = (details: any) => {
          if (!isolated) {
            dispatch({
              type: UPDATE_COUPON_REDUCER,
              payload: { isLoading: false, details: details || null },
            });
          }
          callback(details);
        };

        if (response.isSuccess && response.data) {
          const raw =
            response.data?._id || response.data?.couponCode
              ? response.data
              : response.data?.data || response.data?.result || response.data;
          const mapped = mapCouponDetailView(raw);
          attachCouponImageUrls([mapped]).then((items) => {
            finish(items[0] || mapped);
          });
          return;
        }

        finish(null);
      },
      { couponId }
    );
  };
};

/** POST /api/v1/shinology/coupon/create-coupon */
export const createCoupon = (values: any, callback: Function = () => {}) => {
  return (dispatch: Function) => {
    const payload = mapCreateCouponPayload(values);

    dispatch({
      type: UPDATE_COUPON_REDUCER,
      payload: { isLoading: true },
    });

    POST_API_CALL(
      ENDPOINTS.COUPON.CREATE,
      payload,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response, "success");
          dispatch({
            type: UPDATE_COUPON_REDUCER,
            payload: { isLoading: false, page: 1 },
          });
          dispatch(getCouponList());
          callback({ isSuccess: true, data: response.data });
          return;
        }

        dispatch({
          type: UPDATE_COUPON_REDUCER,
          payload: { isLoading: false },
        });
        callback({ isSuccess: false, data: null });
      }
    );
  };
};

/** PATCH /api/v1/shinology/coupon/update-coupon/{id} */
export const updateCoupon = (
  couponId: string,
  values: any,
  callback: Function = () => {},
  replacedS3Urls: string[] = [],
  initialValues: any = null
) => {
  return (dispatch: Function) => {
    if (!couponId) {
      callback({ isSuccess: false, data: null });
      return;
    }

    const payload = initialValues
      ? mapUpdateCouponPayload(initialValues, values)
      : mapCreateCouponPayload(values);

    if (!payload || !Object.keys(payload).length) {
      callback({ isSuccess: false, data: null });
      return;
    }

    dispatch({
      type: UPDATE_COUPON_REDUCER,
      payload: { isLoading: true },
    });

    PATCH_API_CALL(
      `${ENDPOINTS.COUPON.UPDATE}/${couponId}`,
      payload,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response, "success");
          if (replacedS3Urls.length) {
            DELETE_S3_FILES(replacedS3Urls);
          }
          dispatch(getCouponList());
          callback({ isSuccess: true, data: response.data });
          return;
        }

        dispatch({
          type: UPDATE_COUPON_REDUCER,
          payload: { isLoading: false },
        });
        callback({ isSuccess: false, data: null });
      }
    );
  };
};

/** DELETE /api/v1/shinology/coupon/delete-coupon/{id} */
export const deleteCoupon = (
  couponId: string,
  callback: Function = () => {},
  s3Urls: string[] = []
) => {
  return (dispatch: Function) => {
    if (!couponId) {
      callback({ isSuccess: false });
      return;
    }

    dispatch({
      type: UPDATE_COUPON_REDUCER,
      payload: { isLoading: true },
    });

    DELETE_API_CALL(
      `${ENDPOINTS.COUPON.DELETE}/${couponId}`,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response, "success");
          if (s3Urls.length) {
            DELETE_S3_FILES(s3Urls);
          }
          dispatch(getCouponList());
          callback({ isSuccess: true });
          return;
        }

        dispatch({
          type: UPDATE_COUPON_REDUCER,
          payload: { isLoading: false },
        });
        callback({ isSuccess: false });
      }
    );
  };
};
